import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Calendar, Settings, Users, Trash2, Edit, Plus, UserX, Clock } from 'lucide-react';
import { useEmployees } from '../../hooks/useEmployees';
import { useVacationRequests } from '../../hooks/useVacationRequests';
import { ProgressiveDaysDialog } from './ProgressiveDaysDialog';
import { toast } from 'sonner';

export const AdminDaysManager = ({ open, onOpenChange, onDataUpdated }) => {
  const { employees, updateEmployee, deleteEmployee, loadEmployees } = useEmployees();
  const { requests, getRequestsByEmployee, deleteRequest } = useVacationRequests();
  const [adminDays, setAdminDays] = useState(3);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeManager, setShowEmployeeManager] = useState(false);
  const [showProgressiveDaysDialog, setShowProgressiveDaysDialog] = useState(false);
  const [progressiveEmployee, setProgressiveEmployee] = useState(null);
  
  const handleApplyToAll = async () => {
    try {
      for (const employee of employees) {
        await updateEmployee(employee.id, {
          ...employee,
          administrativeDays: adminDays
        });
      }
      toast.success(`Se asignaron ${adminDays} días administrativos a todos los empleados`);
      await loadEmployees();
      if (onDataUpdated) onDataUpdated();
    } catch (error) {
      toast.error('Error al actualizar días administrativos');
      console.error('Error:', error);
    }
  };

  const handleResetAdminDays = async () => {
    try {
      for (const employee of employees) {
        await updateEmployee(employee.id, {
          ...employee,
          administrativeDays: 0
        });
      }
      toast.info('Se han reseteado todos los días administrativos');
      await loadEmployees();
      if (onDataUpdated) onDataUpdated();
    } catch (error) {
      toast.error('Error al resetear días administrativos');
      console.error('Error:', error);
    }
  };

  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar al empleado ${employeeName}? Esta acción no se puede deshacer.`)) {
      try {
        await deleteEmployee(employeeId);
        toast.success(`Empleado ${employeeName} eliminado correctamente`);
        await loadEmployees(); // Recargar la lista
        if (onDataUpdated) onDataUpdated(); // Notificar al dashboard
      } catch (error) {
        toast.error('Error al eliminar empleado');
        console.error('Error:', error);
      }
    }
  };

  const handleEditEmployeeDays = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeManager(true);
  };

  const handleConfigureProgressiveDays = (employee) => {
    setProgressiveEmployee(employee);
    setShowProgressiveDaysDialog(true);
  };

  const handleUpdateEmployee = async (employeeId, updates) => {
    try {
      await updateEmployee(employeeId, updates);
      await loadEmployees();
      if (onDataUpdated) onDataUpdated();
    } catch (error) {
      throw error; // Re-throw para que el dialog maneje el error
    }
  };

  const handleUpdateEmployeeDays = async (updates) => {
    if (selectedEmployee) {
      try {
        await updateEmployee(selectedEmployee.id, {
          ...selectedEmployee,
          ...updates
        });
        toast.success(`Días actualizados para ${selectedEmployee.name}`);
        setSelectedEmployee(null);
        setShowEmployeeManager(false);
        await loadEmployees();
        if (onDataUpdated) onDataUpdated(); // Notificar al dashboard
      } catch (error) {
        toast.error('Error al actualizar días del empleado');
        console.error('Error:', error);
      }
    }
  };

  const currentYear = new Date().getFullYear();
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings size={20} />
            Panel de Administración de Vacaciones {currentYear}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Gestión masiva de días administrativos */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar size={16} />
                Gestión Masiva de Días Administrativos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-days">Días administrativos por año</Label>
                <Input
                  id="admin-days"
                  type="number"
                  value={adminDays}
                  onChange={(e) => setAdminDays(Number(e.target.value))}
                  min="0"
                  max="10"
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Los días administrativos se resetean automáticamente cada 1 de enero
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleApplyToAll}
                  className="flex-1"
                  disabled={employees.length === 0}
                >
                  Aplicar a Todos ({employees.length} empleados)
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleResetAdminDays}
                  disabled={employees.length === 0}
                >
                  Resetear Todos
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Gestión individual de empleados */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users size={16} />
                Gestión Individual de Empleados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {employees.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No hay empleados registrados</p>
                ) : (
                  employees.map((employee) => (
                    <div key={employee.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium">{employee.name}</div>
                        <div className="text-sm text-muted-foreground">
                          RUT: {employee.rut} | Vacaciones: {employee.vacation_days_available || 0} días | Admin: {employee.admin_days_available || 0} días
                        </div>
                        {employee.progressiveDaysStartDate && (
                          <div className="text-xs text-blue-600 mt-1">
                            Días progresivos: {employee.progressiveDays || 0} 
                            {employee.progressiveDaysApprovalDate && ' (aplicados)'}
                            {!employee.progressiveDaysApprovalDate && ' (no aplicados)'}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleConfigureProgressiveDays(employee)}
                          title="Configurar días progresivos"
                        >
                          <Clock size={14} className="mr-1" />
                          Progresivos
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditEmployeeDays(employee)}
                        >
                          <Edit size={14} className="mr-1" />
                          Editar
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteEmployee(employee.id, employee.name)}
                        >
                          <UserX size={14} className="mr-1" />
                          Eliminar
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>

      {/* Dialog para editar días individuales */}
      {selectedEmployee && (
        <Dialog open={showEmployeeManager} onOpenChange={setShowEmployeeManager}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Días - {selectedEmployee.name}</DialogTitle>
            </DialogHeader>
            <EmployeeDaysEditor 
              employee={selectedEmployee}
              onSave={handleUpdateEmployeeDays}
              onCancel={() => {
                setShowEmployeeManager(false);
                setSelectedEmployee(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog para configurar días progresivos */}
      <ProgressiveDaysDialog
        employee={progressiveEmployee}
        open={showProgressiveDaysDialog}
        onOpenChange={setShowProgressiveDaysDialog}
        onUpdateEmployee={handleUpdateEmployee}
      />
    </Dialog>
  );
};

// Componente para editar días individuales
const EmployeeDaysEditor = ({ employee, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    legalDays: employee.legalDays || employee.legal_days || 0,
    progressiveDays: employee.progressiveDays || employee.progressive_days || 0,
    additionalDays: employee.additionalDays || employee.additional_days || 0,
    administrativeDays: employee.administrativeDays || employee.administrative_days || 0,
    usedDays: employee.usedDays || employee.used_days || 0,
    adminDaysUsedThisYear: employee.adminDaysUsedThisYear || employee.admin_days_used_this_year || 0
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: parseInt(value) || 0
    }));
  };

  const handleSubmit = () => {
    onSave({
      legalDays: formData.legalDays,
      progressiveDays: formData.progressiveDays,
      additionalDays: formData.additionalDays,
      administrativeDays: formData.administrativeDays,
      usedDays: formData.usedDays,
      adminDaysUsedThisYear: formData.adminDaysUsedThisYear,
      // También enviar en snake_case para compatibilidad con backend
      legal_days: formData.legalDays,
      progressive_days: formData.progressiveDays,
      additional_days: formData.additionalDays,
      administrative_days: formData.administrativeDays,
      used_days: formData.usedDays,
      admin_days_used_this_year: formData.adminDaysUsedThisYear
    });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="legal-days">Días Legales</Label>
          <Input
            id="legal-days"
            type="number"
            value={formData.legalDays}
            onChange={(e) => handleChange('legalDays', e.target.value)}
            min="0"
          />
        </div>
        <div>
          <Label htmlFor="progressive-days">Días Progresivos</Label>
          <Input
            id="progressive-days"
            type="number"
            value={formData.progressiveDays}
            onChange={(e) => handleChange('progressiveDays', e.target.value)}
            min="0"
          />
        </div>
        <div>
          <Label htmlFor="additional-days">Días Adicionales</Label>
          <Input
            id="additional-days"
            type="number"
            value={formData.additionalDays}
            onChange={(e) => handleChange('additionalDays', e.target.value)}
            min="0"
          />
        </div>
        <div>
          <Label htmlFor="admin-days">Días Administrativos</Label>
          <Input
            id="admin-days"
            type="number"
            value={formData.administrativeDays}
            onChange={(e) => handleChange('administrativeDays', e.target.value)}
            min="0"
          />
        </div>
        <div>
          <Label htmlFor="used-days">Días Usados (Vacaciones)</Label>
          <Input
            id="used-days"
            type="number"
            value={formData.usedDays}
            onChange={(e) => handleChange('usedDays', e.target.value)}
            min="0"
          />
        </div>
        <div>
          <Label htmlFor="admin-used-days">Días Admin Usados {new Date().getFullYear()}</Label>
          <Input
            id="admin-used-days"
            type="number"
            value={formData.adminDaysUsedThisYear}
            onChange={(e) => handleChange('adminDaysUsedThisYear', e.target.value)}
            min="0"
          />
        </div>
      </div>

      <div className="flex gap-2 pt-4">
        <Button onClick={handleSubmit} className="flex-1">
          Guardar Cambios
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </div>
  );
};
